import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Column,
  ConfirmDialog,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  TextField,
} from "stories/Admin";
import styles from "./RegistreringerPage.module.scss";
import useFetch from "hooks/useFetch";

interface CourseInterest {
  id: number;
  name: string;
  phone: string;
  email: string;
  gender: string | null;
  courseName: string | null;
  createdDate: string;
}

const genderLabel = (gender: string | null | undefined): string => {
  if (gender === "MALE") return "Mann";
  if (gender === "FEMALE") return "Dame";
  return "";
};

const RegistreringerPage: React.FC = () => {
  const { execute } = useFetch();
  const [items, setItems] = useState<CourseInterest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState<CourseInterest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 20;

  const fetchAll = async () => {
    setIsLoading(true);
    const result = (await execute("/api/v1/course-interest", "GET")) as
      | CourseInterest[]
      | null;
    if (result) setItems(result);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const courses = useMemo(() => {
    const set = new Set<string>();
    items.forEach((it) => {
      if (it.courseName) set.add(it.courseName);
    });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((it) => {
      const matchesCourse = !courseFilter || it.courseName === courseFilter;
      if (!q) return matchesCourse;
      const haystack = `${it.name} ${it.email} ${it.phone} ${
        it.courseName ?? ""
      } ${genderLabel(it.gender)}`.toLowerCase();
      return matchesCourse && haystack.includes(q);
    });
  }, [items, searchTerm, courseFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered.length]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  const handleDeleteClick = (item: CourseInterest) => {
    setSelected(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    await execute(`/api/v1/course-interest/${selected.id}`, "DELETE");
    setItems((prev) => prev.filter((x) => x.id !== selected.id));
    setShowDeleteModal(false);
    setSelected(null);
  };

  const handleExportCsv = () => {
    const header = ["Navn", "Telefon", "E-post", "Kjønn", "Kurs", "Registrert"];
    const rows = filtered.map((it) => [
      it.name,
      it.phone,
      it.email,
      genderLabel(it.gender),
      it.courseName ?? "",
      new Date(it.createdDate).toLocaleString("no-NO"),
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([`﻿${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `registreringer-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns: Array<Column<CourseInterest>> = [
    { key: "name", header: "Navn", render: (it) => <b>{it.name}</b> },
    {
      key: "email",
      header: "E-post",
      render: (it) => (
        <a className={styles.link} href={`mailto:${it.email}`}>
          {it.email}
        </a>
      ),
    },
    {
      key: "phone",
      header: "Telefon",
      numeric: true,
      render: (it) => (
        <a className={styles.plainLink} href={`tel:${it.phone}`}>
          {it.phone}
        </a>
      ),
    },
    {
      key: "gender",
      header: "Kjønn",
      render: (it) =>
        it.gender ? (
          <Badge tone={it.gender === "MALE" ? "blue" : "lilac"}>
            {genderLabel(it.gender) || it.gender}
          </Badge>
        ) : (
          "–"
        ),
    },
    {
      key: "course",
      header: "Kurs",
      render: (it) =>
        it.courseName ? <Badge tone="mint">{it.courseName}</Badge> : "–",
    },
    {
      key: "created",
      header: "Registrert",
      numeric: true,
      render: (it) => (
        <span
          className={styles.muted}
          title={new Date(it.createdDate).toLocaleString("no-NO")}
        >
          {new Date(it.createdDate).toLocaleDateString("no-NO", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      width: 56,
      render: (it) => (
        <IconButton
          label="Slett"
          size="sm"
          icon={<Icon name="trash" size={16} />}
          onClick={() => handleDeleteClick(it)}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Kursregistreringer"
        subtitle="Personer som har meldt interesse via ibønn.no/registrering."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={fetchAll}
              loading={isLoading}
            >
              Oppdater
            </Button>
            <Button
              variant="secondary"
              icon={<Icon name="download" size={16} />}
              onClick={handleExportCsv}
              disabled={filtered.length === 0}
            >
              Eksporter CSV
            </Button>
          </>
        }
      />

      <div className={styles.filters}>
        <TextField
          aria-label="Søk"
          type="search"
          placeholder="Søk på navn, e-post, telefon, kjønn eller kurs"
          className={styles.search}
          fieldClassName={styles.searchField}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select
          aria-label="Kurs"
          fieldClassName={styles.courseField}
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
        >
          <option value="">Alle kurs</option>
          {courses.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <span className={styles.muted}>
          Viser {filtered.length} av {items.length}
        </span>
      </div>

      <Card className={styles.tableCard}>
        {isLoading && items.length === 0 ? (
          <div className={styles.skeletons}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={currentItems}
            rowKey={(it) => it.id}
            empty={
              <EmptyState title="Ingen registreringer funnet">
                Ingen har meldt interesse ennå, eller ingen passer søket.
              </EmptyState>
            }
          />
        )}
        <Pagination
          page={currentPage}
          pageCount={totalPages}
          onChange={setCurrentPage}
        />
      </Card>

      <ConfirmDialog
        open={showDeleteModal}
        title="Slett registrering?"
        tone="danger"
        confirmLabel="Slett"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      >
        Registreringen for <b>{selected?.name}</b> slettes. Dette kan ikke
        angres.
      </ConfirmDialog>
    </>
  );
};

export default RegistreringerPage;
